import React, { memo, useState } from 'react';
import { LineChart, Loader2, ChevronDown, Sparkles, Map } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { cn } from '../../lib/utils';
import { DynamicChart } from './Charts';
import PlotContainer from './PlotContainer';
import MapLayerCard from './MapLayerCard';

const Message = memo(({ message, onSuggestionClick, onAddLayer, onViewMapLayer }) => {
  const isBot = message.role === 'assistant';
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const isDark = document.documentElement.classList.contains('dark');

  return (
    <div className="flex flex-col mb-6" id={`message-${message.id}`}>
      <div className={cn(
        "flex w-full gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300",
        !isBot && "justify-end"
      )}>
        <div className={cn(
          isBot
            ? "w-full text-gray-800 dark:text-gray-200"
            : "max-w-[85%] rounded-2xl p-4 shadow-sm bg-emerald-50 dark:bg-emerald-800/30 text-black dark:text-black rounded-tr-none"
        )}>
          {/* Agent Steps - Show at top, expanded while loading, minimized when done */}
          {isBot && message.agentSteps && message.agentSteps.length > 0 && (
            <div className={cn(
              "mb-3 pb-3 border-b border-gray-200 dark:border-gray-700",
              !message.isLoading && "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded transition-colors"
            )}
              onClick={() => !message.isLoading && setStepsExpanded(!stepsExpanded)}
            >
              {message.isLoading || stepsExpanded ? (
                // Expanded view - show all steps
                <div className="space-y-1">
                  {message.agentSteps.map((step, idx) => {
                    const isLastStep = idx === message.agentSteps.length - 1;
                    const isActive = isLastStep && message.isLoading;
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400"
                      >
                        {isActive ? (
                          <Loader2 size={12} className="text-blue-500 dark:text-blue-400 animate-spin shrink-0" />
                        ) : (
                          <div className="w-2 h-2 shrink-0 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                          </div>
                        )}
                        <span>{step.step}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Minimized view - show summary
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                  <div className="w-2 h-2 shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                  </div>
                  <span className="flex-1">
                    {message.agentSteps.length} step{message.agentSteps.length !== 1 ? 's' : ''} completed
                  </span>
                  <ChevronDown size={12} className="shrink-0" />
                </div>
              )}
            </div>
          )}

          {message.content && (
            <div className={cn(
              "prose prose-base max-w-none",
              isBot
                ? "prose-gray dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-code:text-emerald-600 dark:prose-code:text-emerald-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-a:text-emerald-600 dark:prose-a:text-emerald-400 hover:prose-a:text-emerald-700 dark:hover:prose-a:text-emerald-300"
                : "prose-headings:text-black dark:prose-headings:text-black prose-p:text-black dark:prose-p:text-black prose-code:text-emerald-800 dark:prose-code:text-emerald-700 prose-code:bg-emerald-200/50 dark:prose-code:bg-emerald-800/30 prose-pre:bg-emerald-200/30 dark:prose-pre:bg-emerald-800/30 prose-pre:border-emerald-400/30 dark:prose-pre:border-emerald-600/30 prose-a:text-emerald-800 dark:prose-a:text-emerald-700 hover:prose-a:text-emerald-900 dark:hover:prose-a:text-emerald-600 prose-blockquote:border-emerald-400/50"
            )}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                  code: ({ node, inline, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : '';
                    const codeString = String(children).replace(/\n$/, '');

                    if (!inline && language) {
                      return (
                        <div className={cn(
                          "my-3 rounded-lg overflow-hidden",
                          isBot
                            ? "border border-gray-200 dark:border-gray-700"
                            : "border border-emerald-500/30"
                        )}>
                          <SyntaxHighlighter
                            language={language}
                            style={isDark ? vscDarkPlus : prism}
                            customStyle={{
                              margin: 0,
                              padding: '1rem',
                              fontSize: '1rem',
                              lineHeight: '1.6',
                              background: isBot
                                ? (isDark ? '#1f2937' : '#f3f4f6')
                                : 'rgba(16, 185, 129, 0.1)',
                            }}
                            codeTagProps={{
                              style: {
                                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                              }
                            }}
                            {...props}
                          >
                            {codeString}
                          </SyntaxHighlighter>
                        </div>
                      );
                    }

                    // Inline code - use prose styling
                    return <code className={className} {...props}>{children}</code>;
                  },
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {message.visualization && !message.chartData && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700 flex items-center gap-3 text-gray-600 dark:text-gray-400">
              <LineChart size={20} className="text-emerald-500" />
              <span className="text-xs font-medium">Visualization: {message.visualization}</span>
            </div>
          )}
          {/* Modern Map Layer Card */}
          {message.isMapResult && message.layer_id && (
            <MapLayerCard
              layerId={message.layer_id}
              name={message.name || message.content?.split('\n')[0] || 'Map Layer'}
              sql={message.sql}
              onViewMap={() => onViewMapLayer && onViewMapLayer(message.layer_id)}
            />
          )}

          {/* Regular Chart/Plot Container */}
          {(message.chartData || message.sql) && !message.isMapResult && (
            <PlotContainer
              data={message.chartData}
              sql={message.sql}
              title={message.spec?.title || message.visualization}
              rowCount={message.rowCount !== undefined ? message.rowCount : (message.chartData ? message.chartData.length : 0)}
              isMapResult={false}
              spec={message.spec}
            >
              {message.chartData && message.spec && (
                <DynamicChart data={message.chartData} spec={message.spec} noContainer />
              )}
            </PlotContainer>
          )}

          {isBot && message.mappable && !message.isMapResult && (
            <div className="mt-4">
              <button
                onClick={() => {
                  if (onAddLayer && !message.mapLayerRequested) {
                    onAddLayer(message.originalQuery);
                  }
                }}
                disabled={message.isGeneratingMap || message.mapLayerRequested}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all",
                  "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400",
                  "hover:bg-emerald-100 dark:hover:bg-emerald-900/30",
                  "border border-emerald-200 dark:border-emerald-800",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {message.isGeneratingMap ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Generating map...</span>
                  </>
                ) : (
                  <>
                    <Map size={16} />
                    <span>Related map</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {isBot && message.suggestions && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 dark:text-gray-400">
            <Sparkles size={12} />
            <span>Suggestions</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {message.suggestions.map((q) => (
              <div
                key={q}
                onClick={() => onSuggestionClick(q)}
                className="px-2.5 py-1 text-xs text-black dark:text-white bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-md cursor-pointer transition-colors"
              >
                {q}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.message === nextProps.message &&
         prevProps.onSuggestionClick === nextProps.onSuggestionClick &&
         prevProps.onAddLayer === nextProps.onAddLayer &&
         prevProps.onViewMapLayer === nextProps.onViewMapLayer;
});

export default Message;
